import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const ORG_TYPES = [
  { value: "club", label: "Club" },
  { value: "agency", label: "Agency" },
  { value: "journalist", label: "Journalist" },
  { value: "agent", label: "Agent" },
];

const emptyForm = { name: "", type: "club", country: "" };

export default function OrganizationsPage() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/organizations");
      setOrgs(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (org) => {
    setEditing(org);
    setForm({ name: org.name, type: org.type, country: org.country || "" });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        country: form.country.trim() || null,
      };
      if (editing) {
        await api.patch(`/organizations/${editing.id}`, payload);
        toast.success("Organization updated");
      } else {
        await api.post("/organizations", payload);
        toast.success("Organization created");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/organizations/${toDelete.id}`);
      toast.success("Organization deleted");
      setToDelete(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
      setToDelete(null);
    }
  };

  return (
    <div data-testid="organizations-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">
            Network
          </div>
          <h1 className="font-display uppercase text-4xl md:text-5xl font-bold tracking-tight">
            Organizations
          </h1>
          <p className="text-muted-foreground mt-2 max-w-lg">
            Clubs, agencies, journalists and agents that operate scouts on the platform.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="h-11 rounded-none font-display uppercase tracking-[0.18em]"
          data-testid="create-organization-button"
        >
          <Plus className="h-4 w-4 mr-2" /> New Organization
        </Button>
      </header>

      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        <Table data-testid="organizations-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Name</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Type</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Country</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="px-6 py-12 text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : orgs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="px-6 py-16 text-center">
                  <Building2 className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                  <div className="font-display uppercase tracking-wider">No organizations yet</div>
                  <div className="text-sm text-muted-foreground mt-1">Create your first one to get started.</div>
                </TableCell>
              </TableRow>
            ) : (
              orgs.map((org) => (
                <TableRow key={org.id} className="tr-fade border-border hover:bg-muted/30" data-testid={`org-row-${org.id}`}>
                  <TableCell className="px-6 py-4 font-medium">{org.name}</TableCell>
                  <TableCell className="px-6 py-4">
                    <Badge variant="secondary" className="rounded-none uppercase tracking-wider text-[10px]">
                      {org.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-muted-foreground">{org.country || "—"}</TableCell>
                  <TableCell className="px-6 py-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(org)}
                        data-testid={`edit-org-${org.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setToDelete(org)}
                        data-testid={`delete-org-${org.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="org-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">
              {editing ? "Edit Organization" : "New Organization"}
            </DialogTitle>
            <DialogDescription>
              {editing ? "Update the details below." : "Create a new organization for the platform."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Name</Label>
              <Input
                id="org-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="org-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-type">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger id="org-type" data-testid="org-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORG_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value} data-testid={`org-type-option-${t.value}`}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-country">Country (optional)</Label>
              <Input
                id="org-country"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                data-testid="org-country-input"
              />
            </div>
            <DialogFooter className="mt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="org-cancel-button">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="org-save-button">
                {saving ? "Saving…" : editing ? "Save changes" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-org-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete organization?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{toDelete?.name}</strong>. Scouts attached to this organization must be reassigned first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-org-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-org-confirm-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
